import React, { useState, useRef } from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import {
    SourceFile as SourceFileType,
    HindsightFileErrors,
    NewRelicEventName,
    NewRelicEventStatus,
    ProcessIdentifiers,
} from 'buyplan-common';
import { v4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { uploadHindsightFile } from '../../services/hindsightService';
import FileDropzone from '../FileDropzone/FileDropzone';
import SourceFile, { fileTypes } from '../SourceFile/SourceFile';
import { sendCustomNewRelicEvent } from '../../actions/user';
import { getProcessStatus } from '../../services/processesService';

interface Props {
    files: SourceFileType[];
    onDelete(uuid: string): void;
    onSuccess(): void;
    onCancelError?: (uuid: string) => void;
    isDisabled?: boolean;
}

function ImportHindsightFile({ onSuccess, files, onDelete, isDisabled, onCancelError }: Props) {
    const [processingFile, setProcessingFile] = useState<SourceFileType & { uuid: string }>();
    const currentUploadIdRef = useRef<string | null>(null);
    const getHindsightProcessStatus = () => getProcessStatus(ProcessIdentifiers.processHindsightFile, true);
    const dispatch = useDispatch();

    const handleUpload = async (uploadFiles: File[]) => {
        if (uploadFiles === null || uploadFiles.length === 0) {
            return;
        }

        // Generate new upload ID and clear previous state to prevent race conditions
        const uploadId = v4();
        currentUploadIdRef.current = uploadId;
        setProcessingFile(undefined);

        dispatch(sendCustomNewRelicEvent(NewRelicEventName.hindsightFileUpload, NewRelicEventStatus.start));
        const currentProcessingFileAttributes = {
            uuid: uploadId,
            progress: 10 as number,
            filename: uploadFiles[0].name,
            filesize: uploadFiles[0].size,
            mimetype: 'text/csv',
        };
        setProcessingFile(currentProcessingFileAttributes);

        const increaseProgress = (percentage: number) => {
            // Only update progress if this is still the current upload
            if (currentUploadIdRef.current !== uploadId) {
                return; // Stale callback, ignore
            }

            currentProcessingFileAttributes.progress += percentage;
            setProcessingFile({ ...currentProcessingFileAttributes });
        };

        try {
            await uploadHindsightFile(uploadFiles[0], uploadId, increaseProgress, getHindsightProcessStatus);

            // Verify this is still the current upload before setting success
            if (currentUploadIdRef.current === uploadId) {
                onSuccess();
                dispatch(sendCustomNewRelicEvent(NewRelicEventName.hindsightFileUpload, NewRelicEventStatus.end));
                setProcessingFile(undefined);
            }
        } catch (err: unknown) {
            // Only handle error if this is still the current upload
            if (currentUploadIdRef.current !== uploadId) {
                return; // Ignore stale errors
            }

            const error = err as { meta: HindsightFileErrors };
            if (error.meta?.requireRollback) {
                onDelete(uploadId);
            }
            dispatch(sendCustomNewRelicEvent(NewRelicEventName.hindsightFileUpload, NewRelicEventStatus.error));

            if (typeof error.meta?.error === 'string') {
                setProcessingFile({
                    ...currentProcessingFileAttributes,
                    progress: undefined,
                    errors: { ...error.meta, system: error.meta.error },
                });
            } else {
                setProcessingFile({
                    ...currentProcessingFileAttributes,
                    progress: undefined,
                    errors: error.meta,
                });
            }
        }
    };

    return (
        <div className="ImportFiles">
            {files?.length > 0 || processingFile ? (
                <TransitionGroup className="ImportFiles__files" component="ul">
                    <CSSTransition
                        key={processingFile ? processingFile.uuid : files[0].uuid}
                        timeout={{
                            enter: 500,
                            exit: 500,
                        }}
                        classNames="ImportFiles__file--animation"
                    >
                        <li>
                            <div className="ImportFiles__file">
                                <SourceFile
                                    file={processingFile || files[0]}
                                    fileType={fileTypes.csv}
                                    onDelete={(fileId: string) => {
                                        onDelete(fileId);
                                        onSuccess();
                                    }}
                                    onReupload={handleUpload}
                                    onCancelError={() => {
                                        const fileId = processingFile?.uuid || files[0]?.uuid;
                                        if (fileId && onCancelError) {
                                            setProcessingFile(undefined);
                                            onCancelError(fileId);
                                            onSuccess();
                                        }
                                    }}
                                    narrow={true}
                                    isDisabled={isDisabled}
                                />
                            </div>
                        </li>
                    </CSSTransition>
                </TransitionGroup>
            ) : (
                <FileDropzone
                    acceptedMimeTypes={fileTypes.csv.mime}
                    title={
                        <>
                            Drag &#39;n drop files here or <span className="button">browse</span>
                        </>
                    }
                    onDrop={handleUpload}
                    multiple={false}
                    isDisabled={isDisabled}
                />
            )}
        </div>
    );
}

export default ImportHindsightFile;
